<script>
import { TEXTO_ANALISE_CONCLUIDA } from '@modules/aawz-intelligence-v2/utils/painel-deliberacao-view'
import { CLASSE_SHIMMER_TEXTO } from '@modules/aawz-intelligence-v2/constants/TextChat'

export default {
  name: 'IntelligenceV2PainelCardVivo',
  props: {
    // Card já montado por `montarCardsVivos`: área, rótulo, ícone, estado e a
    // fala acumulada até agora.
    card: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // Rótulo "analisando…" só enquanto não há texto: assim que a fala começa,
    // ela mesma é a prova de que a área está trabalhando — dois indicadores
    // competiriam.
    mostraEspera() {
      return !this.card.indisponivel && !this.card.fala
    },
    mostraSpinnerDaFala() {
      return Boolean(this.card.fala) && Boolean(this.card.falando)
    },
    // `ia-shimmer-text`, não o `shimmer-text` do protótipo: aquele recorta um
    // gradiente no texto (`background-clip`) e repinta a cada quadro enquanto o
    // markdown re-renderiza a cada token — e some por completo nos quadros em
    // que o gradiente não cobre os glifos. Este pulsa opacidade, que é
    // compositada.
    classeShimmer() {
      return CLASSE_SHIMMER_TEXTO
    },
    // O spinner atravessa os dois momentos: esperando o primeiro token e
    // enquanto a fala cresce. Ele só sai quando a área fecha ou não responde.
    mostraSpinner() {
      return this.mostraEspera || this.mostraSpinnerDaFala
    },
    // Avatar por estado, no desenho do protótipo: sólido na cor da marca
    // enquanto a área fala; neutro aguardando ou indisponível.
    avatarFalando() {
      return this.mostraSpinner
    },
    corDoIconeDoAvatar() {
      return this.mostraSpinner ? 'white' : 'primary'
    },
    // O que a linha de estado do cabeçalho anuncia. Vazio durante o streaming:
    // ali quem informa é a própria fala, que o leitor de tela lê quando o
    // usuário chega nela — e não a cada token.
    textoDeEstado() {
      if (this.card.indisponivel) return ''
      if (this.mostraEspera) return this.card.texto
      if (this.mostraSpinnerDaFala) return ''
      return TEXTO_ANALISE_CONCLUIDA
    },
  },
}
</script>

<template>
  <!-- A área que ainda não entregou o turno. Na rodada 1 a mesa inteira aparece
       desde a abertura; nas seguintes, só quem está falando agora. -->
  <div class="painel-card-vivo az-gap-xsmall">
    <div class="d-flex align-center flex-wrap az-gap-small painel-card-vivo__cabecalho">
      <span
        class="painel-avatar flex-shrink-0"
        :class="avatarFalando ? 'painel-avatar--falando' : 'painel-avatar--pronto'"
        aria-hidden="true"
      >
        <icon-svg :name="card.icone" :size="14" :color="corDoIconeDoAvatar" />
      </span>
      <span class="az-text-primary az-size-12 font-medium">{{ card.label }}</span>
      <BaseStatus
        v-if="card.indisponivel"
        soft
        preserve-case
        :content="card.texto"
        color="warning-new"
      />
      <!-- A única região viva do card: "analisando…" antes do primeiro token e
           "Análise concluída" quando a área fecha. Duas frases curtas por área,
           que é o que um leitor de tela precisa para acompanhar a mesa. -->
      <!-- O shimmer é só pele (spec-007 R3): a região viva e o texto anunciado
           não mudam. -->
      <output
        v-if="textoDeEstado"
        class="az-text-secondary az-size-12 az-muted"
        :class="mostraEspera ? classeShimmer : ''"
        aria-live="polite"
      >
        {{ textoDeEstado }}
      </output>
    </div>

    <!-- A fala crescendo token a token. Mesma tipografia do turno pronto: quando
         o artefato chega e substitui o acumulado, o texto troca no lugar.
         O `aria-live` fica em `off` de propósito: este texto muda a cada delta
         e, como região viva, faria o leitor de tela recomeçar o parágrafo
         dezenas de vezes por turno. O progresso é anunciado pela linha de
         estado acima. -->
    <div
      v-if="card.fala"
      class="az-text-secondary az-size-12 painel-card-vivo__fala"
      aria-live="off"
    >
      {{ card.fala }}<span v-if="mostraSpinnerDaFala" class="iv2-cursor-digitando" aria-hidden="true" />
    </div>

    <div v-if="card.motivo" class="az-text-secondary az-size-12 az-muted painel-card-vivo__linha">
      {{ card.motivo }}
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'

// Mesma moldura do turno pronto (`painel-deliberacao__turno`): é a mesma área
// falando, antes e depois do artefato chegar — mudar a caixa faria o turno
// "pular" quando o texto final substituísse a prévia.
//
// ⚠️ Por isso a hairline e o `padding-top` saíram daqui em 19/09/2026, junto
// com os do turno: no redesenho a RODADA virou a linha de lista (é ela que tem
// o filete), e manter o filete também entre as falas desenhava duas réguas
// aninhadas — o cartão lia como tabela. Quem separa as áreas dentro da rodada
// é o avatar e o nome em negrito, mais o `az-gap-small` do container.
.painel-card-vivo
  display: flex
  flex-direction: column

.painel-avatar
  display: inline-flex
  align-items: center
  justify-content: center
  width: 28px
  height: 28px
  border-radius: 999px

.painel-avatar--pronto
  background: var(--neutral-100)

.painel-avatar--falando
  background: var(--primary)

.painel-card-vivo__fala
  line-height: 1.5
  white-space: pre-wrap
  margin-left: 36px

.painel-card-vivo__linha
  line-height: 1.45
  margin-left: 36px

// O wrapper 0×0 do `v-tooltip` do BaseStatus conta como item de flex e dobraria
// o gap ao redor do chip.
::v-deep .painel-card-vivo__cabecalho .v-tooltip
  display: contents
</style>
